import { locales } from '~/i18n/constants'
import { storefrontLocales } from '~/i18n/storefrontLocales'
import { hreflangClaims } from '~/i18n/hreflangCountries'
import cookie from 'js-cookie'
import { ref, computed } from '#imports'

export default defineNuxtPlugin(async () => {
  const route = useRoute()

  const locale = ref('en-gb')

  // Storefront, not Content, Locales: the Shopify store reads the locale off the
  // route, and if this fell back to en-gb for /en-de the currency-keyed product
  // cache would never hit.
  const localeProperties = computed(() => {
    return storefrontLocales.find((storefrontLocale) => storefrontLocale.code === locale.value)
  })

  function setLocale(localeCode) {
    cookie.set('i18n_redirected', localeCode, { expires: 28 })
    locale.value = localeCode
  }

  /**
   *
   * We set the locale before all else so that the correct market is set for commerce layer
   * amongst other things.
   *
   * It's vital this is done before anything else happens on the site to avoid
   * showing the wrong prices for the wrong locale.
   *
   * We handle this ourselves due to the no_prefix setting on the i18n package.
   *
   * Here we pluck the locale from URL init $i18n with that locale so that all other
   * functionality later that hooks into $i18n is localised correctly.
   *
   */

  const matchedLocale = storefrontLocales.find((locale) => {
    return route.params.locale === locale.code || route.fullPath.startsWith('/' + locale.code)
  })

  if (matchedLocale) {
    await setLocale(matchedLocale.code)
  } else {
    await setLocale(locale.value)
  }

  function getLocaleHead() {
    const unwantedQueryParamPatterns = [/^utm_/i, /^ref/i, /^fbclid$/i, /^gclid$/i, /^ab-/i]

    const queryParams = new URLSearchParams(route.query)

    // Filter out unwanted query parameters
    unwantedQueryParamPatterns.forEach((pattern) => {
      for (const param of queryParams.keys()) {
        if (pattern.test(param)) {
          queryParams.delete(param)
        }
      }
    })

    // Construct the clean path
    const cleanPath = route.path + (queryParams.toString() ? `?${queryParams}` : '')

    const metaObject = {
      htmlAttrs: {
        lang: localeProperties.value.iso,
      },
      link: [] as any[],
      meta: [
        {
          property: 'og:locale',
          content: localeProperties.value.iso,
          hid: 'i18n-og-locale',
        },
        {
          property: 'og:url',
          content: `https://www.sungod.co${cleanPath}`,
          hid: 'og:url',
        },
      ],
    }

    const url = (code) =>
      `https://www.sungod.co${route.path.replace(/^\/([a-z]{2}-[a-z]{2})/, '/' + code)}`

    const current = localeProperties.value
    const isContentLocale = locales.some((l) => l.code === current?.code)

    metaObject.link.push({
      rel: 'canonical',
      href: url(current?.contentLocale || 'en-gb'),
      hid: 'i18n-canonical',
    })

    if (isContentLocale) {
      locales.forEach((l) => {
        metaObject.link.push({
          rel: 'alternate',
          hreflang: l.iso.toLowerCase(),
          href: url(l.code),
          hid: `i18n-alt-${l.code}`,
        })
      })

      metaObject.link.push(
        { rel: 'alternate', hreflang: 'en', href: url('en-gb'), hid: 'i18n-alt-en' },
        { rel: 'alternate', hreflang: 'x-default', href: url('en-gb'), hid: 'i18n-alt-x-default' }
      )

      hreflangClaims(locales).forEach(({ country, localeCode }) => {
        const hreflang = `en-${country.toLowerCase()}`

        metaObject.link.push({
          rel: 'alternate',
          hreflang,
          href: url(localeCode),
          hid: `i18n-alt-${hreflang}`,
        })
      })
    }

    return metaObject
  }

  /**
   *
   *
   * We provide these as a way to stub the @nuxtjs/i18n module and make some
   * of its methods and properties available in the same way the official
   * module would.
   *
   * At the time of writing the module had not stabalised for Nuxt 3.
   *
   */

  return {
    provide: {
      i18n: {
        locale,
        locales,
        localeProperties,
        getLocaleHead,
      },
    },
  }
})
